import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { servicesIndexCopy } from '../content/pages';

/**
 * Три питання перед блогом.
 *
 * Між «Як ми працюємо» і «Блогом» лишалася смуга порожнечі на пів екрана —
 * власник це помітив. Заповнювати її декором не було сенсу: на головній і
 * так уже є фото, малюнки й стрічка. Тут стоїть те, що людині справді
 * потрібно на цьому місці сторінки: три найчастіші питання з відповідями
 * в одне речення й посилання на повний список.
 *
 * Текст не новий — це перші три питання з того самого масиву, що живить
 * сторінку /pytannya. Дублювати відповіді словами не можна: дві копії
 * розійшлися б. Відповідь тут скорочена до першого речення, повна —
 * за посиланням.
 *
 * Смуга бірюзова, як стрічка тем вище: два однакові акценти на сторінці
 * читаються як система, а третій новий колір — як випадковість.
 */
/**
 * Перше речення відповіді — але не коротше за 40 знаків: у питання «Ви
 * працюєте по всій Україні?» відповідь починається зі слова «Так.», і сама
 * по собі вона нічого не пояснює. Тоді беремо два речення.
 */
const shortAnswer = (text) => {
  let cut = 0;
  while (cut < text.length) {
    const next = text.indexOf('. ', cut);
    if (next === -1) return text;
    cut = next + 2;
    if (cut >= 40) return text.slice(0, cut).trim();
  }
  return text;
};

const QUESTIONS = servicesIndexCopy.faq.slice(0, 3);

const FaqTeaser = () => {
  const [ref, inView] = useInView({ threshold: 0.15, triggerOnce: true });

  return (
    <section className="py-14 lg:py-20 bg-teal-50 border-y border-teal-100" ref={ref}>
      <div className="section-container">
        <div className="grid gap-8 lg:gap-14 lg:grid-cols-12 items-start">
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7 }}
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-px bg-gradient-to-r from-teal-500 to-sky-400 flex-shrink-0"></span>
              <span className="text-teal-700 uppercase tracking-[0.2em] text-[0.7rem] sm:text-xs font-semibold">
                Коротко про головне
              </span>
            </div>
            <h2 className="text-[1.75rem] sm:text-3xl lg:text-[2.5rem] font-extrabold font-display text-slate-900 leading-[1.15] tracking-tight">
              Питають найчастіше
            </h2>
            <Link
              to="/pytannya"
              className="mt-6 inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
            >
              Усі запитання й відповіді
              <Icon name="fa-arrow-right" />
            </Link>
          </motion.div>

          <div className="lg:col-span-8 grid gap-4 sm:grid-cols-3">
            {QUESTIONS.map((entry, index) => (
              <motion.div
                key={entry.q}
                className="rounded-2xl bg-white border border-teal-100 p-5 lg:p-6 flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + index * 0.1 }}
              >
                <span className="text-xs font-bold tracking-[0.2em] text-teal-700 mb-3">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 className="font-display font-bold text-slate-900 leading-snug">{entry.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{shortAnswer(entry.a)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqTeaser;
